import type { QueryClient } from '@tanstack/react-query';

/**
 * Optimistic cache surgery for `user_items`.
 *
 * Inventory rows live in three differently-shaped caches:
 *   - `['my-items', userId]`            → UserItem[]
 *   - `['user-item', idOrShortId]`      → UserItem (single, keyed by id OR short_id)
 *   - `['user-collection', userId, vis]`→ { items: UserItem[], stats }
 *
 * These helpers patch/remove a row across all three without caring which
 * surface the mutation was fired from, so price edits, list/unlist toggles
 * and deletes paint instantly and roll back cleanly on error.
 */

const ITEM_KEY_PREFIXES = [['my-items'], ['user-item'], ['user-collection']] as const;

type Snapshot = [readonly unknown[], unknown][];

function isMatch(row: any, id: string): boolean {
	return !!row && (row.id === id || row.short_id === id);
}

function transformCacheValue(
	value: any,
	id: string,
	apply: (row: any) => any | null,
): any {
	if (!value) return value;

	// UserItem[]
	if (Array.isArray(value)) {
		let changed = false;
		const next: any[] = [];
		for (const row of value) {
			if (!isMatch(row, id)) {
				next.push(row);
				continue;
			}
			changed = true;
			const patched = apply(row);
			if (patched !== null) next.push(patched);
		}
		return changed ? next : value;
	}

	// { items, stats }
	if (Array.isArray(value.items)) {
		const nextItems = transformCacheValue(value.items, id, apply);
		if (nextItems === value.items) return value;
		const listed = nextItems.filter((i: any) => i?.visibility === 'listed').length;
		return {
			...value,
			items: nextItems,
			stats: value.stats ? { ...value.stats, total: nextItems.length, listed } : value.stats,
		};
	}

	// Single UserItem
	if (isMatch(value, id)) return apply(value);

	return value;
}

function mutateItemCaches(
	queryClient: QueryClient,
	id: string,
	apply: (row: any) => any | null,
): Snapshot {
	const snapshot: Snapshot = [];

	for (const prefix of ITEM_KEY_PREFIXES) {
		const entries = queryClient.getQueriesData({ queryKey: prefix as unknown as readonly unknown[] });
		for (const [key, value] of entries) {
			const next = transformCacheValue(value, id, apply);
			if (next !== value) {
				snapshot.push([key, value]);
				queryClient.setQueryData(key, next);
			}
		}
	}

	return snapshot;
}

/** Merge `patch` into the cached row everywhere it appears. */
export function patchItemInCaches(
	queryClient: QueryClient,
	id: string,
	patch: Record<string, any>,
): Snapshot {
	return mutateItemCaches(queryClient, id, (row) => ({ ...row, ...patch }));
}

/** Drop the row from every list cache (single-item caches are nulled). */
export function removeItemFromCaches(queryClient: QueryClient, id: string): Snapshot {
	return mutateItemCaches(queryClient, id, () => null);
}

/** Restore a snapshot taken by the helpers above (error rollback). */
export function restoreItemCaches(queryClient: QueryClient, snapshot?: Snapshot) {
	if (!snapshot) return;
	for (const [key, value] of snapshot) {
		queryClient.setQueryData(key, value);
	}
}

/** Stop in-flight item queries so they can't clobber the optimistic paint. */
export async function cancelItemQueries(queryClient: QueryClient) {
	await Promise.all(
		ITEM_KEY_PREFIXES.map((prefix) =>
			queryClient.cancelQueries({ queryKey: prefix as unknown as readonly unknown[] }),
		),
	);
}

/**
 * Bulk variant — patch many rows in one pass over the caches. Returns a single
 * snapshot that restores everything at once.
 */
export function patchItemsInCaches(
	queryClient: QueryClient,
	ids: string[],
	patch: Record<string, any>,
): Snapshot {
	const idSet = new Set(ids);
	const snapshot: Snapshot = [];

	const applyToValue = (value: any): any => {
		if (!value) return value;
		if (Array.isArray(value)) {
			let changed = false;
			const next = value.map((row: any) => {
				if (!row || (!idSet.has(row.id) && !idSet.has(row.short_id))) return row;
				changed = true;
				return { ...row, ...patch };
			});
			return changed ? next : value;
		}
		if (Array.isArray(value.items)) {
			const nextItems = applyToValue(value.items);
			if (nextItems === value.items) return value;
			const listed = nextItems.filter((i: any) => i?.visibility === 'listed').length;
			return {
				...value,
				items: nextItems,
				stats: value.stats ? { ...value.stats, total: nextItems.length, listed } : value.stats,
			};
		}
		if (idSet.has(value.id) || idSet.has(value.short_id)) return { ...value, ...patch };
		return value;
	};

	for (const prefix of ITEM_KEY_PREFIXES) {
		const entries = queryClient.getQueriesData({ queryKey: prefix as unknown as readonly unknown[] });
		for (const [key, value] of entries) {
			const next = applyToValue(value);
			if (next !== value) {
				snapshot.push([key, value]);
				queryClient.setQueryData(key, next);
			}
		}
	}

	return snapshot;
}
